import { z } from 'zod';

// ============================================================
// THE TWO-LAYER APP LOCK, AS THE TABLET IS ALLOWED TO SEE IT.
//
// `settings.schema.ts` declares the model (`AppLockSettingsSchema`) and states
// the split: layer 1 is a short OPEN pin for everyday use of a shared device,
// layer 2 a separate ADMIN pin that gates Settings. It was built end to end on
// the server and in the web console and reached the APK — the one deployment it
// was designed for — not at all. This block is that wire.
//
// THE ASYMMETRY IS THE WHOLE DESIGN, and it is a security decision rather than
// a convenience:
//
//   OPEN  — RETIRED (owner, 2026-09-17: one PIN). The tablet opens to
//           operator sign-in. `hasOpenPin` is always false on the wire, so an
//           older APK never shows an open pad it cannot pass, and no open-PIN
//           verifier is shipped any more.
//
//   ADMIN — SHIPPED AS ITS BCRYPT HASH SINCE 2026-09-28, by the owner's
//           decision, and what follows is the argument that decision
//           overrode, kept because it states the cost being accepted. A
//           device-owner tablet is locked to Hadir, so a site with no signal
//           could not open Settings to fix its own Wi-Fi: the lock had no way
//           out offline. `adminPinVerifier` is the company PIN's bcrypt hash;
//           the device checks against it only when the server cannot answer,
//           and throttles its own wrong guesses. The risk, stated to the owner
//           and accepted: a stolen AND rooted tablet could brute-force the
//           company-wide PIN. Before that decision: Settings
//           re-points the device at another server, rebinds its worksite and
//           wipes the biometric gallery; that is a materially stronger boundary
//           than "open the app", and the same 10,000-candidate arithmetic that
//           is acceptable for layer 1 is not acceptable for layer 2. So the
//           admin layer is verified SERVER-SIDE (POST /api/field/app-lock/
//           verify, device-token, throttled per device) and an offline device
//           simply cannot open Settings. That is a real cost and the device
//           says so out loud rather than pretending — see §3.11 in
//           docs/design-rules.md and `components/settings/AdminLockGate.tsx`.
//           An admin PIN that only LOOKS like a lock is worse than none.
//
// `hasOpenPin` / `hasAdminPin` mirror `AppLockStatus`: booleans, never hashes.
// The device needs `hasAdminPin` to know whether to gate Settings at all — a
// company that never set one keeps today's ungated screen rather than acquiring
// a door with no key.
// ============================================================
export const FieldAppLockSchema = z.object({
  enabled: z.boolean(),
  hasOpenPin: z.boolean(),
  hasAdminPin: z.boolean(),
  /**
   * `security.pinLength` — how many digits BOTH lock PINs are, company-wide.
   * `validatePinStrength` enforces it on every write, so this is not advice:
   * a device that assumed 4 would submit a truncated candidate on a 6-digit
   * tenant and report a correct PIN as wrong. It is also how many dots the pad
   * draws, which is the only cue an operator has for how long the PIN is.
   */
  pinLength: z.number().int().min(4).max(8),
  /** The settings PIN's bcrypt hash, for the offline check only; null when
   *  no settings PIN is set. Absent from servers older than 2026-09-28. */
  adminPinVerifier: z.string().nullable().optional(),
});
export type FieldAppLock = z.infer<typeof FieldAppLockSchema>;

/** The device asking the server whether a typed ADMIN pin is the admin pin. */
export const FieldAppLockVerifySchema = z.object({
  pin: z.string().regex(/^\d{4,8}$/),
});

